import React, { useEffect, useState } from "react";
import "./SettingsPage.scss";
import { TabView, TabPanel } from "primereact/tabview";
import { ProfileTab } from "../components/ProfileTab";
import { AccountTab } from "../components/AccountTab";
import { NotificationsTab } from "../components/NotificationsTab";
import { SubscriptionTab } from "../components/SubscriptionTab";
import { BillingTab } from "../components/BillingTab";
import { SettingsTabsViewModel } from "@/features/settings/viewmodel/SettingsTabsViewModel";
import {
  $userBasicInfo,
  $userLoading,
  fetchUserBasicInfoRequested,
} from "@/features/user/store";
import Tracking from "@/core/utils/tracking";
import { OrganizationTab } from "../components/OrganizationTab";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTES } from "@/core/constants/app";
import { SEO } from "@/core/components/SEO/SEO";
import { useUnit } from "effector-react";
import Loader from "@/core/components/loader/Loader";

export const SettingsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, loading, refetchProfile } = SettingsTabsViewModel();
  const basicInfo = useUnit($userBasicInfo);
  const loadingBasicInfo = useUnit($userLoading);
  const fetchBasicInfo = useUnit(fetchUserBasicInfoRequested);
  const [showBilling, setShowBilling] = useState(false);

  // Standard effect to fetch user info if it's not already available
  useEffect(() => {
    if (!basicInfo && !loadingBasicInfo) {
      fetchBasicInfo();
    }
  }, [basicInfo, loadingBasicInfo, fetchBasicInfo]);

  // Effect to force a refresh when landing on subscription status pages
  useEffect(() => {
    const isOnSubscriptionStatusRoute =
      location.pathname === ROUTES.SETTINGS_SUBSCRIPTION_SUCCESS ||
      location.pathname === ROUTES.SETTINGS_SUBSCRIPTION_FAIL;

    if (isOnSubscriptionStatusRoute) {
      fetchBasicInfo();
    }
  }, [location.pathname, fetchBasicInfo]);

  // ✅ Type as readonly array of strings (not literal types)
  const TAB_ROUTES = [
    ROUTES.SETTINGS_PROFILE,
    ROUTES.SETTINGS_ACCOUNT,
    ROUTES.SETTINGS_NOTIFICATIONS,
    ROUTES.SETTINGS_ORGANIZATION,
    ROUTES.SETTINGS_SUBSCRIPTION,
    ROUTES.SETTINGS_BILLING,
    ROUTES.SETTINGS_SUBSCRIPTION_SUCCESS,
    ROUTES.SETTINGS_SUBSCRIPTION_FAIL,
  ] as const;

  const getActiveIndexFromPath = (): number => {
    const currentPath = location.pathname;

    // Handle nested organization routes
    if (currentPath.startsWith(ROUTES.SETTINGS_ORGANIZATION)) {
      return TAB_ROUTES.findIndex(
        (route) => route === ROUTES.SETTINGS_ORGANIZATION,
      );
    }

    // Handle nested subscription routes
    if (
      currentPath.startsWith(ROUTES.SETTINGS_SUBSCRIPTION) ||
      currentPath === ROUTES.SETTINGS_SUBSCRIPTION_SUCCESS ||
      currentPath === ROUTES.SETTINGS_SUBSCRIPTION_FAIL
    ) {
      return TAB_ROUTES.findIndex(
        (route) => route === ROUTES.SETTINGS_SUBSCRIPTION,
      );
    }

    // Default to profile if on /settings
    if (currentPath === ROUTES.SETTINGS) {
      return 0;
    }

    // Find matching route for top-level tabs
    const availableRoutes = showBilling
      ? [...TAB_ROUTES]
      : TAB_ROUTES.filter((route) => route !== ROUTES.SETTINGS_BILLING);

    const index = availableRoutes.findIndex((route) => route === currentPath);
    return index >= 0 ? index : 0;
  };

  const activeIndex = getActiveIndexFromPath();

  // ✅ Redirect /settings to /settings/profile
  useEffect(() => {
    if (location.pathname === ROUTES.SETTINGS) {
      navigate(ROUTES.SETTINGS_PROFILE, { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (basicInfo?.is_stripe_customer) {
      setShowBilling(true);
    } else {
      setShowBilling(false);
    }
  }, [basicInfo]);

  useEffect(() => {
    Tracking.pageView(
      "Settings",
      location.pathname,
      undefined,
      undefined,
      undefined,
      document.title,
      undefined,
      undefined,
    );
  }, [location.pathname]);

  useEffect(() => {
    const appLayout = document.querySelector(".app-layout") as HTMLElement;
    if (appLayout) {
      appLayout.style.background = "#fff";
    }

    return () => {
      if (appLayout) {
        appLayout.style.backgroundColor = "";
      }
    };
  }, []);

  const subscriptionStatus =
    location.pathname === ROUTES.SETTINGS_SUBSCRIPTION_SUCCESS
      ? "success"
      : location.pathname === ROUTES.SETTINGS_SUBSCRIPTION_FAIL
        ? "fail"
        : undefined;

  const handleTabChange = (index: number) => {
    const availableRoutes = showBilling
      ? [...TAB_ROUTES]
      : TAB_ROUTES.filter((route) => route !== ROUTES.SETTINGS_BILLING);

    const route = availableRoutes[index];
    if (route) {
      navigate(route as string); // ✅ Cast to string for navigate
    }
  };

  if (loading) {
    return <Loader message="Loading settings..." />;
  }

  return (
    <div className="settings-page">
      <SEO title={`Settings | EUcalls`} description={`Settings`} />

      <div className="pageTitle">Settings</div>

      <div className="settings-body">
        <TabView
          activeIndex={activeIndex}
          onTabChange={(e) => handleTabChange(e.index)}
        >
          <TabPanel header="Profile">
            <ProfileTab />
          </TabPanel>
          <TabPanel header="Account">
            <AccountTab />
          </TabPanel>
          <TabPanel header="Notifications">
            <NotificationsTab />
          </TabPanel>
          <TabPanel header="Organization">
            <OrganizationTab />
          </TabPanel>
          <TabPanel header="Subscription">
            <SubscriptionTab status={subscriptionStatus} />
          </TabPanel>
          {showBilling && (
            <TabPanel header="Billing">
              <BillingTab />
            </TabPanel>
          )}
        </TabView>
      </div>
    </div>
  );
};
